/**
 * Chaînes de l'interface, français et anglais.
 *
 * Reprises de `values/strings.xml` et `values-fr/strings.xml`. Les clés sont
 * identiques à celles de l'application Android pour que les deux versions
 * restent comparables ligne à ligne.
 */

export type Locale = 'fr' | 'en'

export const STRINGS = {
  fr: {
    app_name: 'VeloGPX-Web',

    // Navigation générale
    open_gpx: 'Ouvrir GPX',
    my_location: 'Ma position',
    track_list: 'Liste des traces',
    track_info: 'Infos trace',
    elevation_profile: "Profil d'altitude",
    rename_track: 'Renommer',
    delete_track: 'Supprimer',
    delete_track_confirm_title: 'Supprimer la trace',
    delete_track_confirm_message:
      'Supprimer {name} ? Cette action est irréversible.',
    hide_track: 'Masquer la trace',
    show_track: 'Afficher la trace',
    change_color: 'Changer la couleur',
    no_tracks_loaded: 'Aucune trace chargée',
    zoom_in: 'Zoom avant',
    zoom_out: 'Zoom arrière',
    satellite_view: 'Vue satellite',
    menu: 'Menu',
    fullscreen: 'Plein écran',
    exit_fullscreen: 'Quitter le plein écran',
    install_app: "Installer l'application",
    add_to_home_screen: "Installer sur l'écran d'accueil",
    add_to_home_screen_intro:
      "Safari ne sait pas passer une page web en plein écran sur iPhone. Installer VeloGPX-Web sur l'écran d'accueil donne mieux : ni barre d'adresse, ni barre d'onglets.",
    add_to_home_screen_step_share: 'Appuyez sur Partager, dans la barre de Safari',
    add_to_home_screen_step_add: "Choisissez « Sur l'écran d'accueil »",
    add_to_home_screen_step_open: "Ouvrez VeloGPX-Web depuis l'écran d'accueil",
    settings: 'Réglages',
    reset: 'Réinitialiser',
    close: 'Fermer',
    cancel: 'Annuler',
    ok: 'OK',
    compass_reset_north: 'Boussole, appuyez pour orienter au nord',
    guidance_mode: 'Mode guidage',
    reorder_track: 'Réordonner',
    error_not_gpx_file: "Ce fichier n'est pas un fichier GPX",
    error_not_gpx_files: '{count} fichiers ignorés : seuls les GPX sont acceptés',
    error_import_empty_selection:
      'Aucun fichier reçu. Depuis iCloud, ouvrez-le une fois dans Fichiers pour le télécharger, puis réessayez.',
    error_import_empty_file:
      '« {name} » est arrivé vide. Depuis iCloud, ouvrez-le une fois dans Fichiers pour le télécharger, puis réessayez.',
    error_gpx_no_content:
      '« {name} » est bien un GPX, mais ne contient ni trace ni point d’intérêt.',
    error_gpx_too_large:
      '« {name} » dépasse {limit} Mo et n’a pas été ouvert. Un GPX de course en fait quelques-uns.',
    error_track_not_drawn:
      'Trace importée, mais la carte n’a pas pu l’afficher. Rouvrez l’application.',
    error_import_failed: 'Import interrompu : {reason}',
    routed_track: 'Itinéraire routier calculé',
    track_settings: 'Réglages de la trace',
    track_settings_title: 'Réglages : {name}',
    track_settings_km_row: 'Départ réel et espacement',
    track_settings_show_waypoints: "Afficher les points d'intérêt",
    error_waypoints_reload:
      "Impossible de charger les points d'intérêt depuis le fichier source",
    km_markers: 'Points kilométriques',
    km_marker_custom_interval: 'Espacement (km)',
    km_marker_interval_value: '{value} km',
    km_from_fictive_start: 'Compter depuis le départ fictif',
    fix_position: 'Corriger la position',
    fix_position_not_available:
      'Disponible en roulant, sur une trace qui repasse au même endroit',

    // Réglages — catégories
    settings_section_general: 'Général',
    settings_cat_general_desc: 'Langue, thème',
    settings_section_map: 'Carte',
    settings_cat_map_desc: 'Style, bâtiments 3D',
    settings_section_interface: 'Interface',
    settings_cat_interface_desc: 'Boutons, HUD, taille des icônes',
    settings_section_navigation: 'Navigation',
    settings_cat_navigation_desc:
      "Recentrage, indicateur km, roadbook, profil d'altitude",
    settings_section_markers: 'Marqueurs',
    settings_cat_markers_desc: 'Points km, marqueurs spécifiques',
    radars_section: 'Radars',
    settings_cat_radars_desc: 'Radars fixes',
    settings_section_about: 'À propos',
    settings_cat_about_desc: 'Version, composants, licence, stockage',

    // Réglages — entrées
    language: 'Langue',
    language_system: 'Par défaut (système)',
    language_english: 'English',
    language_french: 'Français',
    theme: 'Thème',
    theme_system: 'Par défaut (système)',
    theme_light: 'Clair',
    theme_dark: 'Sombre',
    map_style: 'Style de carte',
    show_3d_buildings: 'Bâtiments 3D',
    show_3d_buildings_summary_available: 'Disponible avec le style actuel',
    show_3d_buildings_summary_unavailable: 'Non disponible avec le style actuel',
    show_zoom_buttons: 'Afficher les boutons de zoom',
    show_guidance_button: 'Afficher le bouton guidage',
    location_icon_size: 'Taille de la flèche (mode suivi)',
    show_speed_altitude: 'Afficher vitesse et altitude',
    show_altitude: "Afficher l'altitude",
    speed_altitude_size: 'Taille du texte',
    relock_delay_label: 'Délai de recentrage auto',
    relock_delay_value: '{value} s',
    km_indicator_label: 'Indicateur kilométrique',
    roadbook_label: 'Roadbook',
    roadbook_marker_count_portrait: 'Marqueurs (portrait)',
    roadbook_marker_count_landscape: 'Marqueurs (paysage)',
    roadbook_text_size: 'Taille du texte',
    roadbook_disappear_distance: 'Distance de disparition',
    marker_alert_label: 'Alerte marqueurs',
    marker_alert_distance: "Distance d'alerte",
    marker_alert_size: 'Taille des symboles',
    elevation_profile_hud_label: "Profil d'altitude",
    elevation_profile_hud_range: 'Distance visible devant',
    elevation_profile_hud_range_behind: 'Distance visible derrière',

    // Marqueurs
    show_km_markers: 'Afficher les points km',
    km_marker_interval_label: 'Espacement des points km',
    marker_size_label: 'Taille des marqueurs',
    custom_marker_dark_mode: 'Marqueurs spécifiques foncés',
    predefined_markers: 'Marqueurs prédéfinis',
    edit_symbol: 'Modifier le symbole : {name}',
    custom_markers: 'Marqueurs spécifiques',
    add_marker: 'Ajouter un marqueur',
    edit_marker: 'Modifier le marqueur',
    marker_move: 'Déplacer le marqueur',
    marker_move_hint: 'Touchez la nouvelle position sur la trace',
    marker_type: 'Type',
    marker_name: 'Nom',
    marker_symbol: 'Symbole',
    marker_distance_km: 'Distance (km)',
    marker_description: 'Description',
    no_custom_markers:
      'Aucun marqueur spécifique.\nAppuyez sur + pour en ajouter.',
    delete_marker_confirm: 'Supprimer ce marqueur ?',
    marker_type_gpm: 'GPM',
    marker_type_sprint: 'Sprint',
    marker_type_feed: 'Ravitaillement',
    marker_type_waste: 'Déchets',
    marker_type_yellowflag: 'Drapeau Jaune',
    marker_type_radar: 'Radar',
    marker_type_wayout: 'Way out',

    // Détours (way in/out)
    edit_way: 'Détour',
    way_exit_km: 'Sortie (km)',
    way_entry_km: 'Rentrée (km)',
    way_entry_change: 'Modifier le point de rentrée',
    way_pick_entry_hint: 'Touchez la trace au point de rentrée',
    way_pick_via_hint: 'Touchez la carte au point de passage',
    way_via_title: 'Point de passage',
    way_via_message:
      'Ajouter un point de passage pour forcer le détour hors du parcours ?',
    way_via_more_message_one: '1 point de passage posé. En ajouter un autre ?',
    way_via_more_message: '{count} points de passage posés. En ajouter un autre ?',
    way_via_pick: 'Choisir sur la carte',
    way_via_skip: 'Sans détour',
    way_via_done: "Calculer l'itinéraire",
    way_via_state_none: 'Point de passage : aucun',
    way_via_state_count: 'Points de passage : {count}',
    way_via_add: 'Ajouter un point de passage',
    way_via_remove_all: 'Retirer tous les points de passage',
    way_via_remove_all_confirm_one: 'Retirer le point de passage ?',
    way_via_remove_all_confirm: 'Retirer les {count} points de passage ?',
    way_via_move: 'Déplacer',
    way_via_delete: 'Supprimer',
    way_via_delete_confirm: 'Supprimer ce point de passage ?',
    way_recalculate: "Recalculer l'itinéraire",
    way_delete_confirm: 'Supprimer ce détour ?',
    way_routing_error: "Le calcul de l'itinéraire du détour a échoué",
    way_routing_in_progress: "Calcul de l'itinéraire…",
    marker_type_derivation: 'Dérivation',
    marker_type_other: 'Autre',
    derivation_side_title: 'Dérivation : de quel côté ?',
    derivation_side_left: 'À gauche',
    derivation_side_right: 'À droite',
    derivation_replace_confirm:
      'Une dérivation existe déjà (km {km}). La remplacer ?',
    derivation_replace: 'Remplacer',
    real_start_type: 'Départ réel',
    real_start_replace_confirm:
      "Une trace n'a qu'un seul départ réel. Il est déjà posé au km {km} : le déplacer ici ?",
    real_start_move: 'Déplacer',
    marker_add: 'Ajouter',
    marker_edit_action: 'Régler',
    marker_delete: 'Supprimer',
    marker_visible: 'Visible',
    marker_symbol_hint: '2 caractères au maximum',
    marker_move_cancelled: 'Déplacement annulé',
    marker_moved: 'Marqueur déplacé au km {km}',
    marker_added: 'Marqueur ajouté au km {km}',
    marker_out_of_track: 'Aucune trace à cet endroit',
    marker_distance_invalid: 'Distance invalide',

    // Trace et infos
    real_start_distance: 'Distance de départ réel (km)',
    elevation_fetch: "Récupérer l'altitude",
    elevation_progress: 'Altitude',
    elevation_applied: 'Altitude récupérée · D+ {gain} m',
    elevation_error: "Échec de la récupération d'altitude",
    elevation_offer_title: "Récupérer l'altitude ?",
    elevation_offer_message:
      "{count} trace(s) importée(s) n'ont pas d'altitude : ni profil, ni dénivelé. Les récupérer en ligne ?",
    elevation_offer_later: 'Plus tard',
    routing_profile_label: 'Profil de routage',
    routing_profile_car: 'Voiture',
    routing_profile_bike: 'Vélo',
    routing_profile_foot: 'À pied',
    routing_in_progress: 'Itinéraire',
    cancel_routing: "Annuler le calcul d'itinéraire",
    routing_success: 'Itinéraire routier calculé',
    routing_error: "Le calcul de l'itinéraire a échoué",
    action_calculate_route: "Calculer l'itinéraire routier",
    action_revert_waypoints: "Revenir aux points d'origine",
    real_start_at: 'Départ réel : {value} km',
    real_start_section_label: 'Depuis le départ réel ({value} km)',
    real_start_moved: 'Départ réel déplacé au km {km}',
    track_info_format: '{distance} km · D+ {gain} m',
    track_info_format_no_ele: '{distance} km',
    info_distance_format: 'Distance : {value} km',
    info_elevation_gain_format: 'Dénivelé positif : {value} m',
    info_elevation_loss_format: 'Dénivelé négatif : {value} m',
    info_altitude_range_format: 'Altitude : {min} m à {max} m',
    source_type_label: 'Source : {value}',
    speed_format: '{value} km/h',
    speed_no_data: '-- km/h',
    altitude_format: '{value} m',
    altitude_no_data: '-- m',
    track_default_name: 'Trace {index}',
    long_press_choose_km:
      'La trace passe plusieurs fois ici. À quel point kilométrique ?',
    long_press_choose_track: 'Quelle trace ?',
    long_press_km_option: 'km {value}',

    // Import et export
    import_choice_title: 'Que souhaitez-vous importer ?',
    import_all: 'Tout (la trace + {waypoints} points d\'intérêt)',
    import_tracks_only: 'Trace uniquement',
    import_select_track_title_named: '{name} — quelle trace ?',
    import_track_fallback_name: 'Trace {index}',
    import_track_entry: '{name} — {km} km',
    route_sparse_title: 'Trace en lignes droites',
    route_sparse_prompt_one:
      "Cette trace n'a qu'un point tous les {spacing} m : elle coupe les virages en ligne droite au lieu de suivre la route. En roulant, l'accrochage à la trace lâchera souvent, et avec lui l'indicateur kilométrique, le roadbook et les alertes de marqueurs. Le kilométrage est sous-estimé.\n\nCalculer l'itinéraire routier maintenant ?",
    route_sparse_prompt_other:
      "{count} traces importées n'ont qu'un point tous les {spacing} m : elles coupent les virages en ligne droite au lieu de suivre la route. En roulant, l'accrochage à la trace lâchera souvent, et avec lui l'indicateur kilométrique, le roadbook et les alertes de marqueurs. Le kilométrage est sous-estimé.\n\nCalculer les itinéraires routiers maintenant ?",
    route_cache_failed:
      "L'itinéraire n'a pas pu être enregistré (espace insuffisant ?). Il sera perdu au prochain démarrage — libérez de la place et recalculez-le.",
    route_sparse_go: 'Calculer',
    route_sparse_later: 'Plus tard',
    import_waypoints_only: "Points d'intérêt uniquement ({count})",
    hud_preview: 'Aperçu',
    hud_preview_all: 'Aperçu de tous les panneaux',
    close_preview: 'Fermer l’aperçu',
    export_gpx: 'Exporter',
    export_gpx_dialog_title: 'Exporter la trace : {name}',
    export_gpx_save_local: "Enregistrer sur l'appareil",
    export_gpx_error: "L'export a échoué.",
    export_gpx_share: 'Partager',
    export_gpx_saved: 'Fichier GPX enregistré',
    export_gpx_failed: "Échec de l'export de la trace",

    // Radars
    radars_enable: 'Afficher les radars',
    radars_countries: 'Pays',
    radars_refresh: 'Rafraîchir',
    radars_last_refresh: 'Dernière mise à jour : {date}',
    radars_never_refreshed: 'Pas encore téléchargé',
    radars_refresh_ok: '{count} radars chargés',
    radars_refresh_error: 'Échec du téléchargement',
    startup_warning_title: 'Avant de partir',
    startup_warning_safety:
      "VeloGPX ne doit pas détourner votre attention de la route. Réglez votre parcours à l'arrêt : tenir un téléphone en main en roulant est interdit dans de nombreux pays, France comprise. Les traces, les itinéraires calculés et les altitudes peuvent être inexacts ou périmés, et un itinéraire calculé peut emprunter une voie interdite, impraticable ou dangereuse à vélo — il ne remplace ni la signalisation, ni le code de la route, ni votre jugement. N'utilisez pas l'application comme moyen de secours. Vous restez seul responsable de votre conduite et de votre sécurité.",
    startup_warning_storage:
      "VeloGPX ne dépose aucun cookie. L'application enregistre sur votre appareil vos réglages, les traces et fichiers GPX que vous importez, et des caches de fond de carte, d'itinéraires et d'altitudes : rien de tout cela n'en sort. En revanche, afficher la carte, calculer un itinéraire ou récupérer des altitudes transmet les coordonnées concernées et votre adresse IP à ce serveur, puis aux services qui répondent — OpenFreeMap, OpenStreetMap, OpenTopoData, Open-Meteo et Overpass. Aucun compte n'est créé et aucun profil n'est constitué. Les caches se vident depuis Réglages > À propos ; effacer les données du site dans votre navigateur retire le reste, traces comprises.",
    startup_warning_info_title: 'Information',
    startup_warning_scope:
      "En acceptant, vous acceptez l'avertissement de sécurité et les termes de la licence ci-dessus. Le paragraphe « Information » ne vous demande rien : il décrit ce que l'application enregistre et transmet pour rendre le service que vous demandez.",
    startup_warning_legal_link: 'Lire les mentions légales',
    startup_warning_gdpr_link: 'Lire la politique de confidentialité',
    startup_warning_scroll_hint:
      'Faites défiler le texte jusqu’au bout pour pouvoir accepter.',
    startup_warning_accept: 'Accepter',
    startup_warning_refuse: 'Refuser',
    startup_warning_dismiss: 'Ne plus afficher ce message',
    startup_refused_title: 'Conditions non acceptées',
    startup_refused_message:
      "VeloGPX Web ne peut pas être utilisé sans l'acceptation de l'avertissement de sécurité et des termes de la licence. Aucune trace n'a été chargée et votre position n'est pas suivie. Vous pouvez revenir à l'avertissement pour le relire, ou simplement fermer cet onglet.",
    startup_refused_back: 'Revenir à l’avertissement',
    radars_warning_title: 'Avertissement',
    radars_warning_message:
      "Les positions des radars proviennent d'OpenStreetMap et sont fournies par la communauté : elles peuvent être incomplètes ou obsolètes. La légalité des avertisseurs de radars varie selon les pays — ils sont notamment interdits en Suisse et en Allemagne. Vous restez seul responsable du respect de la réglementation locale.",
    radars_warning_accept: "J'ai compris",
    radar_popup_title: '{type} : {speed} km/h',
    radar_check_none: 'Aucun radar sur le parcours',
    radar_check_found_one: '1 radar ajouté en marqueur',
    radar_check_found: '{count} radars ajoutés en marqueurs',
    radar_check_need_db: 'Téléchargez la base de radars dans les réglages',
    radar_check: 'Vérification des radars',
    radars_refreshing_country: 'Téléchargement : {country}',
    radars_refreshing_country_progress: 'Téléchargement : {country} : {size}',
    radars_processing: 'Traitement',

    // Altitude

    // À propos
    about_version_label: 'Version',
    about_author_label: 'Auteur',
    about_components_title: 'Composants',
    about_data_sources_title: 'Sources de données',
    about_legal_title: 'Mentions légales',
    about_legal_text:
      "Éditeur : Corentin Jacquemet, particulier, sans activité professionnelle ni but lucratif. Contact : {contact}. Hébergeur : {host}. VeloGPX Web est un projet personnel, mis en ligne pour rendre service ; il n'est adossé à aucune structure et ne fait l'objet d'aucune exploitation commerciale.",
    about_gdpr_title: 'Données personnelles',
    about_gdpr_text:
      "Responsable de traitement : Corentin Jacquemet ({contact}).\n\nDonnées traitées : les coordonnées que vous demandez de calculer ou d'afficher, et votre adresse IP. Aucun compte, aucun identifiant, aucune mesure d'audience.\n\nFinalité et base légale : afficher la carte, calculer un itinéraire, récupérer des altitudes et signaler les radars — c'est-à-dire exécuter le service que vous demandez explicitement.\n\nDestinataires : OpenFreeMap et VersaTiles pour le fond de carte, OpenStreetMap pour le calcul d'itinéraire, OpenTopoData et Open-Meteo pour les altitudes, Overpass pour les radars. Les serveurs sont hébergés en Suisse, qui bénéficie d'une décision d'adéquation de la Commission européenne.\n\nDurées de conservation : itinéraires 30 jours, altitudes 365 jours, radars 7 jours ; les journaux du serveur suivent la politique de l'hébergeur ({logRetention}). Les caches ne sont reliés à aucun identifiant.\n\nVos droits : accès, rectification, effacement, opposition et limitation, en écrivant à {contact}. Vous pouvez adresser une réclamation à la CNIL (www.cnil.fr). Les données que l'application garde sur votre appareil s'effacent depuis Réglages > À propos, ou en effaçant les données du site dans votre navigateur.",
    about_license_title: 'Licence',
    // Condensé du `LICENSE` du dépôt, et non de la chaîne d'Android : les deux
    // produits n'ont pas les mêmes termes. L'application Android est privée et
    // soumet son installation à autorisation ; la version web est **publique à
    // l'utilisation**, seuls son hébergement et sa redistribution restant
    // réservés. Afficher ici le texte de l'APK annoncerait une restriction qui
    // n'existe pas.
    license_text:
      'VeloGPX Web est la version web de l’application VeloGPX. ' +
      '© 2025-2026 Corentin Jacquemet, tous droits réservés. Son utilisation ' +
      'est libre et publique ; son hébergement et sa redistribution sont ' +
      'soumis à l’autorisation expresse et préalable de l’auteur. Toute copie, ' +
      'modification ou diffusion non autorisée constitue une contrefaçon au ' +
      'sens du Code de la propriété intellectuelle et expose son auteur à des ' +
      'poursuites. Le logiciel est fourni en l’état, sans aucune garantie. Les ' +
      'composants tiers et les données cartographiques restent régis par leurs ' +
      'licences respectives. Les présentes conditions sont régies par le droit ' +
      'français ; tout litige relève des tribunaux de Paris, sous réserve des ' +
      'règles impératives protégeant le consommateur. La version française ' +
      'fait foi.',
    about_privacy_title: 'Vie privée',
    about_privacy_text:
      "Aucun compte n'est créé et aucun profil n'est constitué. Vos traces, vos marqueurs et vos réglages restent sur votre appareil. Afficher la carte, calculer un itinéraire ou récupérer des altitudes transmet en revanche les coordonnées concernées et votre adresse IP aux services qui répondent.",
    about_storage_title: 'Stockage',
    about_storage_unknown: 'Taille non communiquée par le navigateur',
    // Un nombre d'entrées, non un poids : le navigateur ne pèse pas ses caches
    // séparément, et les relire un par un rendrait la section inouvrable.
    about_cache_tiles: 'Tuiles de carte : {value}',
    about_cache_routes: 'Itinéraires : {value}',
    about_cache_radars: 'Zones de radars : {value}',
    about_cache_elevations: 'Profils d’altitude : {value}',
    about_storage_estimate_note:
      'Estimation du navigateur : elle ne se met à jour qu’au bout de plusieurs '
      + 'dizaines de secondes, et ne bougera donc pas tout de suite après une purge.',
    about_clear_cache: 'Vider le cache',
    about_clear_cache_confirm_title: 'Vider le cache ?',
    about_clear_cache_confirm_message:
      'Seront supprimés : les tuiles de carte enregistrées pour rouler hors ' +
      'ligne, la base de radars, les itinéraires calculés et les altitudes ' +
      'récupérées. Tout est retéléchargé au besoin, mais il y faut du réseau — ' +
      'et plusieurs minutes pour les radars.\n\n' +
      'Vos traces, vos marqueurs, vos détours et vos réglages ne sont pas ' +
      'touchés.',
    about_cache_cleared: 'Cache vidé',

    // Messages d'état
    location_denied: 'Accès à la position refusé',
    location_unavailable: 'Position indisponible',
    offline: 'Hors ligne',
  },

  en: {
    app_name: 'VeloGPX-Web',

    open_gpx: 'Open GPX',
    my_location: 'My location',
    track_list: 'Track list',
    track_info: 'Track info',
    elevation_profile: 'Elevation profile',
    rename_track: 'Rename',
    delete_track: 'Delete',
    delete_track_confirm_title: 'Delete track',
    delete_track_confirm_message: 'Delete {name}? This cannot be undone.',
    hide_track: 'Hide track',
    show_track: 'Show track',
    change_color: 'Change color',
    no_tracks_loaded: 'No tracks loaded',
    zoom_in: 'Zoom in',
    zoom_out: 'Zoom out',
    satellite_view: 'Satellite view',
    menu: 'Menu',
    fullscreen: 'Fullscreen',
    exit_fullscreen: 'Exit fullscreen',
    install_app: 'Install app',
    add_to_home_screen: 'Add to Home Screen',
    add_to_home_screen_intro:
      'Safari cannot take a web page fullscreen on iPhone. Installing VeloGPX-Web on the Home Screen does better: no address bar, no tab bar.',
    add_to_home_screen_step_share: 'Tap Share in the Safari toolbar',
    add_to_home_screen_step_add: 'Choose “Add to Home Screen”',
    add_to_home_screen_step_open: 'Open VeloGPX-Web from the Home Screen',
    settings: 'Settings',
    reset: 'Reset',
    close: 'Close',
    cancel: 'Cancel',
    ok: 'OK',
    compass_reset_north: 'Compass, tap to reset north',
    guidance_mode: 'Guidance mode',
    reorder_track: 'Reorder',
    error_not_gpx_file: 'This file is not a GPX file',
    error_not_gpx_files: '{count} files ignored: only GPX files are accepted',
    error_import_empty_selection:
      'No file received. From iCloud, open it once in Files to download it, then try again.',
    error_import_empty_file:
      '“{name}” arrived empty. From iCloud, open it once in Files to download it, then try again.',
    error_gpx_no_content:
      '“{name}” is a GPX file, but contains neither track nor waypoint.',
    error_gpx_too_large:
      '“{name}” is over {limit} MB and was not opened. A race GPX is a few MB at most.',
    error_track_not_drawn:
      'Track imported, but the map could not display it. Reopen the app.',
    error_import_failed: 'Import stopped: {reason}',
    routed_track: 'Road route calculated',
    track_settings: 'Track settings',
    track_settings_title: 'Settings: {name}',
    track_settings_km_row: 'Real start and intervals',
    track_settings_show_waypoints: 'Show waypoints',
    error_waypoints_reload: 'Unable to load waypoints from the source file',
    km_markers: 'Kilometer markers',
    km_marker_custom_interval: 'Spacing (km)',
    km_marker_interval_value: '{value} km',
    km_from_fictive_start: 'Reference from fictive start',
    fix_position: 'Fix position',
    fix_position_not_available:
      'Available while riding, on a track that passes the same place twice',

    settings_section_general: 'General',
    settings_cat_general_desc: 'Language, theme',
    settings_section_map: 'Map',
    settings_cat_map_desc: 'Style, 3D buildings',
    settings_section_interface: 'Interface',
    settings_cat_interface_desc: 'Buttons, HUD, icon sizes',
    settings_section_navigation: 'Navigation',
    settings_cat_navigation_desc:
      'Auto-recenter, km indicator, roadbook, elevation profile',
    settings_section_markers: 'Markers',
    settings_cat_markers_desc: 'Km markers, custom markers',
    radars_section: 'Speed cameras',
    settings_cat_radars_desc: 'Fixed speed cameras',
    settings_section_about: 'About',
    settings_cat_about_desc: 'Version, components, license, storage',

    language: 'Language',
    language_system: 'System default',
    language_english: 'English',
    language_french: 'Français',
    theme: 'Theme',
    theme_system: 'System default',
    theme_light: 'Light',
    theme_dark: 'Dark',
    map_style: 'Map style',
    show_3d_buildings: '3D buildings',
    show_3d_buildings_summary_available: 'Available with current style',
    show_3d_buildings_summary_unavailable: 'Not available with current style',
    show_zoom_buttons: 'Show zoom buttons',
    show_guidance_button: 'Show guidance button',
    location_icon_size: 'Arrow size (tracking mode)',
    show_speed_altitude: 'Show speed and altitude',
    show_altitude: 'Show altitude',
    speed_altitude_size: 'Text size',
    relock_delay_label: 'Auto-recenter delay',
    relock_delay_value: '{value} s',
    km_indicator_label: 'Kilometer indicator',
    roadbook_label: 'Roadbook',
    roadbook_marker_count_portrait: 'Markers (portrait)',
    roadbook_marker_count_landscape: 'Markers (landscape)',
    roadbook_text_size: 'Text size',
    roadbook_disappear_distance: 'Disappear distance',
    marker_alert_label: 'Marker alert',
    marker_alert_distance: 'Alert distance',
    marker_alert_size: 'Symbol size',
    elevation_profile_hud_label: 'Elevation profile',
    elevation_profile_hud_range: 'Range ahead',
    elevation_profile_hud_range_behind: 'Range behind',

    show_km_markers: 'Show km markers',
    km_marker_interval_label: 'Km marker spacing',
    marker_size_label: 'Marker size',
    custom_marker_dark_mode: 'Dark custom markers',
    predefined_markers: 'Predefined markers',
    edit_symbol: 'Edit symbol: {name}',
    custom_markers: 'Custom markers',
    add_marker: 'Add marker',
    edit_marker: 'Edit marker',
    marker_move: 'Move marker',
    marker_move_hint: 'Tap the new position on the track',
    marker_type: 'Type',
    marker_name: 'Name',
    marker_symbol: 'Symbol',
    marker_distance_km: 'Distance (km)',
    marker_description: 'Description',
    no_custom_markers: 'No custom markers.\nTap + to add one.',
    delete_marker_confirm: 'Delete this marker?',
    marker_type_gpm: 'GPM',
    marker_type_sprint: 'Sprint',
    marker_type_feed: 'Feed zone',
    marker_type_waste: 'Waste',
    marker_type_yellowflag: 'Yellow flag',
    marker_type_radar: 'Speed camera',
    marker_type_wayout: 'Way out',

    // Way in/out
    edit_way: 'Way in/out',
    way_exit_km: 'Exit (km)',
    way_entry_km: 'Re-entry (km)',
    way_entry_change: 'Change the re-entry point',
    way_pick_entry_hint: 'Tap the track at the re-entry point',
    way_pick_via_hint: 'Tap the map at the via point',
    way_via_title: 'Via point',
    way_via_message: 'Add a via point to force the detour off the course?',
    way_via_more_message_one: '1 via point set. Add another?',
    way_via_more_message: '{count} via points set. Add another?',
    way_via_pick: 'Pick on the map',
    way_via_skip: 'No detour',
    way_via_done: 'Compute the route',
    way_via_state_none: 'Via point: none',
    way_via_state_count: 'Via points: {count}',
    way_via_add: 'Add a via point',
    way_via_remove_all: 'Remove all via points',
    way_via_remove_all_confirm_one: 'Remove the via point?',
    way_via_remove_all_confirm: 'Remove the {count} via points?',
    way_via_move: 'Move',
    way_via_delete: 'Delete',
    way_via_delete_confirm: 'Delete this via point?',
    way_recalculate: 'Recalculate the route',
    way_delete_confirm: 'Delete this way in/out?',
    way_routing_error: 'Way in/out route calculation failed',
    way_routing_in_progress: 'Calculating the route…',
    marker_type_derivation: 'Diversion',
    marker_type_other: 'Other',
    derivation_side_title: 'Diversion: which side?',
    derivation_side_left: 'Left',
    derivation_side_right: 'Right',
    derivation_replace_confirm: 'A diversion already exists (km {km}). Replace it?',
    derivation_replace: 'Replace',
    real_start_type: 'Real start',
    real_start_replace_confirm:
      'A track has only one real start. It is already set at km {km}: move it here?',
    real_start_move: 'Move',
    marker_add: 'Add',
    marker_edit_action: 'Settings',
    marker_delete: 'Delete',
    marker_visible: 'Visible',
    marker_symbol_hint: 'Two characters at most',
    marker_move_cancelled: 'Move cancelled',
    marker_moved: 'Marker moved to km {km}',
    marker_added: 'Marker added at km {km}',
    marker_out_of_track: 'No track here',
    marker_distance_invalid: 'Invalid distance',

    real_start_distance: 'Real start distance (km)',
    elevation_fetch: 'Fetch elevation',
    elevation_progress: 'Elevation',
    elevation_applied: 'Elevation fetched · D+ {gain} m',
    elevation_error: 'Elevation fetch failed',
    elevation_offer_title: 'Fetch elevation?',
    elevation_offer_message:
      '{count} imported track(s) have no elevation: no profile, no ascent. Fetch it online?',
    elevation_offer_later: 'Later',
    routing_profile_label: 'Routing profile',
    routing_profile_car: 'Car',
    routing_profile_bike: 'Bike',
    routing_profile_foot: 'Foot',
    routing_in_progress: 'Route',
    cancel_routing: 'Cancel route calculation',
    routing_success: 'Road route calculated',
    routing_error: 'Route calculation failed',
    action_calculate_route: 'Calculate road route',
    action_revert_waypoints: 'Revert to original points',
    real_start_at: 'Real start: {value} km',
    real_start_section_label: 'From real start ({value} km)',
    real_start_moved: 'Real start moved to km {km}',
    track_info_format: '{distance} km · D+ {gain} m',
    track_info_format_no_ele: '{distance} km',
    info_distance_format: 'Distance: {value} km',
    info_elevation_gain_format: 'Elevation gain: {value} m',
    info_elevation_loss_format: 'Elevation loss: {value} m',
    info_altitude_range_format: 'Altitude: {min} m to {max} m',
    source_type_label: 'Source: {value}',
    speed_format: '{value} km/h',
    speed_no_data: '-- km/h',
    altitude_format: '{value} m',
    altitude_no_data: '-- m',
    track_default_name: 'Track {index}',
    long_press_choose_km:
      'The track passes here several times. At which kilometre point?',
    long_press_choose_track: 'Which track?',
    long_press_km_option: 'km {value}',

    import_choice_title: 'What to import?',
    import_all: 'Everything (track + {waypoints} waypoints)',
    import_tracks_only: 'Track only',
    import_select_track_title_named: '{name} — which track?',
    import_track_fallback_name: 'Track {index}',
    import_track_entry: '{name} — {km} km',
    route_sparse_title: 'Straight-line track',
    route_sparse_prompt_one:
      'This track has only one point every {spacing} m: it cuts corners in straight lines instead of following the road. While riding, snapping to the track will drop out often, taking the distance indicator, the roadbook and marker alerts with it. Distance is underestimated.\n\nCalculate the road route now?',
    route_sparse_prompt_other:
      '{count} imported tracks have only one point every {spacing} m: they cut corners in straight lines instead of following the road. While riding, snapping to the track will drop out often, taking the distance indicator, the roadbook and marker alerts with it. Distance is underestimated.\n\nCalculate the road routes now?',
    route_cache_failed:
      'The route could not be saved (not enough storage?). It will be lost when the app restarts — free some space and calculate it again.',
    route_sparse_go: 'Calculate',
    route_sparse_later: 'Later',
    import_waypoints_only: 'Waypoints only ({count})',
    hud_preview: 'Preview',
    hud_preview_all: 'Preview all panels',
    close_preview: 'Close preview',
    export_gpx: 'Export',
    export_gpx_dialog_title: 'Export track: {name}',
    export_gpx_save_local: 'Save to file',
    export_gpx_error: 'Export failed.',
    export_gpx_share: 'Share',
    export_gpx_saved: 'GPX file saved',
    export_gpx_failed: 'Could not export track',

    radars_enable: 'Show speed cameras',
    radars_countries: 'Countries',
    radars_refresh: 'Refresh',
    radars_last_refresh: 'Last update: {date}',
    radars_never_refreshed: 'Not yet downloaded',
    radars_refresh_ok: '{count} speed cameras loaded',
    radars_refresh_error: 'Download failed',
    startup_warning_title: 'Before you ride',
    startup_warning_safety:
      'VeloGPX must not take your attention away from the road. Plan your route before setting off: holding a phone while riding is prohibited in many countries. Tracks, calculated routes and elevations may be inaccurate or out of date, and a calculated route may use roads that are prohibited, unsuitable or dangerous for cycling — it replaces neither road signs, nor traffic law, nor your own judgement. Do not rely on the app in an emergency. You remain solely responsible for your riding and your safety.',
    startup_warning_storage:
      'VeloGPX sets no cookies. The app stores your settings on your device, along with the tracks and GPX files you import and caches of map tiles, routes and elevations: none of that leaves it. Displaying the map, calculating a route or fetching elevations does however send the coordinates involved and your IP address to this server, and on to the services that answer — OpenFreeMap, OpenStreetMap, OpenTopoData, Open-Meteo and Overpass. No account is created and no profile is built. Caches can be cleared from Settings > About; clearing the site data in your browser removes the rest, tracks included.',
    startup_warning_info_title: 'Information',
    startup_warning_scope:
      'By accepting, you accept the safety warning and the licence terms above. The “Information” paragraph asks nothing of you: it describes what the app stores and transmits in order to provide the service you request.',
    startup_warning_legal_link: 'Read the legal notice',
    startup_warning_gdpr_link: 'Read the privacy policy',
    startup_warning_scroll_hint:
      'Scroll to the end of the text to be able to accept.',
    startup_warning_accept: 'Accept',
    startup_warning_refuse: 'Decline',
    startup_warning_dismiss: 'Do not show this message again',
    startup_refused_title: 'Terms not accepted',
    startup_refused_message:
      'VeloGPX Web cannot be used without accepting the safety warning and the licence terms. No track has been loaded and your position is not being tracked. You can go back to the warning to read it again, or simply close this tab.',
    startup_refused_back: 'Back to the warning',
    radars_warning_title: 'Warning',
    radars_warning_message:
      'Speed camera locations come from OpenStreetMap and are contributed by the community: they may be incomplete or out of date. The legality of speed camera warnings varies by country — they are notably prohibited in Switzerland and Germany. You remain solely responsible for complying with local regulations.',
    radars_warning_accept: 'I understand',
    radar_popup_title: '{type}: {speed} km/h',
    radar_check_none: 'No speed camera on the route',
    radar_check_found_one: '1 speed camera added as a marker',
    radar_check_found: '{count} speed cameras added as markers',
    radar_check_need_db: 'Download the speed camera database in the settings',
    radar_check: 'Speed camera check',
    radars_refreshing_country: 'Downloading: {country}',
    radars_refreshing_country_progress: 'Downloading: {country}: {size}',
    radars_processing: 'Processing',


    about_version_label: 'Version',
    about_author_label: 'Author',
    about_components_title: 'Components',
    about_data_sources_title: 'Data sources',
    about_legal_title: 'Legal notice',
    about_legal_text:
      'Publisher: Corentin Jacquemet, a private individual, with no professional activity and no commercial purpose. Contact: {contact}. Host: {host}. VeloGPX Web is a personal project, published to be useful; it is backed by no organisation and is not commercially exploited.',
    about_gdpr_title: 'Personal data',
    about_gdpr_text:
      'Data controller: Corentin Jacquemet ({contact}).\n\nData processed: the coordinates you ask to compute or display, and your IP address. No account, no identifier, no analytics.\n\nPurpose and legal basis: displaying the map, calculating routes, fetching elevations and flagging speed cameras — that is, performing the service you explicitly request.\n\nRecipients: OpenFreeMap and VersaTiles for map tiles, OpenStreetMap for routing, OpenTopoData and Open-Meteo for elevations, Overpass for speed cameras. Servers are hosted in Switzerland, which benefits from a European Commission adequacy decision.\n\nRetention: routes 30 days, elevations 365 days, speed cameras 7 days; server logs follow the host policy ({logRetention}). Caches are not linked to any identifier.\n\nYour rights: access, rectification, erasure, objection and restriction, by writing to {contact}. You may lodge a complaint with the CNIL (www.cnil.fr). Data the app keeps on your device can be cleared from Settings > About, or by clearing the site data in your browser.',
    about_license_title: 'License',
    license_text:
      'VeloGPX Web is the web version of the VeloGPX application. ' +
      '© 2025-2026 Corentin Jacquemet, all rights reserved. Use is free and ' +
      'open to the public; hosting and redistribution are subject to the ' +
      'express prior authorization of the author. Any unauthorized copy, ' +
      'modification or distribution constitutes copyright infringement under ' +
      'applicable intellectual property law and may lead to legal action. ' +
      'The software is provided as-is, without any warranty. Third-party ' +
      'components and map data remain governed by their own licenses. These ' +
      'terms are governed by French law; any dispute falls to the courts of ' +
      'Paris, subject to mandatory consumer-protection rules. The French ' +
      'version prevails.',
    about_privacy_title: 'Privacy',
    about_privacy_text:
      'No account is created and no profile is built. Your tracks, markers and settings stay on your device. Displaying the map, calculating a route or fetching elevations does however send the coordinates involved and your IP address to the services that answer.',
    about_storage_title: 'Storage',
    about_storage_unknown: 'Size not reported by the browser',
    about_cache_tiles: 'Map tiles: {value}',
    about_cache_routes: 'Routes: {value}',
    about_cache_radars: 'Speed camera areas: {value}',
    about_cache_elevations: 'Elevation profiles: {value}',
    about_storage_estimate_note:
      'Browser estimate: it only refreshes after tens of seconds, so it will '
      + 'not move right after a purge.',
    about_clear_cache: 'Clear cache',
    about_clear_cache_confirm_title: 'Clear the cache?',
    about_clear_cache_confirm_message:
      'This deletes the map tiles saved for offline riding, the speed camera ' +
      'database, computed routes and fetched elevations. Everything is ' +
      'downloaded again when needed, but that takes a connection — and ' +
      'several minutes for the speed cameras.\n\n' +
      'Your tracks, markers, detours and settings are left untouched.',
    about_cache_cleared: 'Cache cleared',

    location_denied: 'Location access denied',
    location_unavailable: 'Location unavailable',
    offline: 'Offline',
  },
} as const satisfies Record<Locale, Record<string, string>>

export type StringKey = keyof (typeof STRINGS)['fr']
